import { Box, Tooltip, Text } from '@mantine/core'
import { useState } from 'react'

export interface AxisLabelProps {
  id?: string
  label: string
  type: 'source' | 'destination'
  highlighted?: boolean
  truncated?: boolean
  maxWidth?: number
  maxHeight?: number
}

export function AxisLabel({ 
  id,
  label, 
  type, 
  highlighted = false, 
  truncated = false,
  maxWidth = 120,
  maxHeight = 40
}: AxisLabelProps) {
  const [isHovered, setIsHovered] = useState(false)
  
  // Determine if text should be truncated based on length and available space
  const shouldTruncate = truncated || label.length > 15
  
  const labelContent = (
    <Box
      data-testid={id ? `axis-label-${type}-${id}` : `axis-label-${type}`}
      data-highlighted={highlighted}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        padding: type === 'source' ? '6px 12px' : '8px 6px',
        maxWidth: type === 'source' ? maxWidth : undefined,
        maxHeight: type === 'destination' ? maxHeight : undefined,
        minWidth: type === 'source' ? '80px' : undefined,
        minHeight: type === 'destination' ? '32px' : undefined,
        backgroundColor: highlighted 
          ? 'var(--mantine-color-blue-1)' 
          : isHovered 
            ? 'var(--mantine-color-gray-1)' 
            : 'var(--mantine-color-gray-0)',
        border: `2px solid ${
          highlighted 
            ? 'var(--mantine-color-blue-4)' 
            : isHovered 
              ? 'var(--mantine-color-blue-2)' 
              : 'var(--mantine-color-gray-3)'
        }`,
        borderRadius: 'var(--mantine-radius-sm)',
        cursor: 'default',
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)', // Smooth broadcast-quality transitions
        willChange: 'background-color, border-color, box-shadow', // Performance optimization for animations
        display: 'flex',
        alignItems: 'center',
        justifyContent: type === 'source' ? 'center' : 'flex-start',
        overflow: 'hidden',
        boxShadow: highlighted 
          ? 'var(--mantine-shadow-sm)' 
          : isHovered 
            ? 'var(--mantine-shadow-xs)' 
            : 'none',
        // Enhanced accessibility
        outline: 'none' // Custom focus handling via theme globalStyles
      }}
    >
      <Text
        size="sm"
        fw={highlighted ? 600 : 500}
        c={highlighted ? 'blue.8' : 'gray.8'}
        style={{
          textOverflow: shouldTruncate ? 'ellipsis' : 'clip',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          width: '100%',
          textAlign: type === 'source' ? 'center' : 'left',
          letterSpacing: '0.025em', // Slightly improved letter spacing for readability
          lineHeight: 1.3
        }}
      >
        {label}
      </Text>
    </Box>
  )

  // If text is truncated or hovered, show tooltip with full label
  if (shouldTruncate || isHovered) {
    return (
      <Tooltip 
        label={label} 
        position={type === 'source' ? 'bottom' : 'right'}
        withArrow
        disabled={!shouldTruncate && !isHovered}
      >
        {labelContent}
      </Tooltip>
    )
  }

  return labelContent
}